import { create } from 'zustand';
import type { IInsight } from '../../ai/interfaces/IInsight';

const DISMISSED_STORAGE_KEY = 'creately_dismissed_insights';

const loadDismissedInsights = (diagramId: string): Set<string> => {
  try {
    const stored = localStorage.getItem(`${DISMISSED_STORAGE_KEY}_${diagramId}`);
    if (!stored) return new Set();
    return new Set(JSON.parse(stored) as string[]);
  } catch {
    return new Set();
  }
};

const saveDismissedInsights = (diagramId: string, dismissed: Set<string>): void => {
  try {
    localStorage.setItem(`${DISMISSED_STORAGE_KEY}_${diagramId}`, JSON.stringify([...dismissed]));
  } catch {
    // Storage full or unavailable
  }
};

interface InsightsState {
  diagramId: string | null;
  insights: IInsight[];
  dismissedInsightIds: Set<string>;

  initialize: (diagramId: string) => void;
  setInsights: (insights: IInsight[]) => void;
  dismissInsight: (id: string) => void;
  dismissAll: () => void;
  undismissInsight: (id: string) => void;
  clearDismissed: () => void;
  getVisibleInsights: () => IInsight[];
}

export const useInsightsStore = create<InsightsState>((set, get) => ({
  diagramId: null,
  insights: [],
  dismissedInsightIds: new Set(),

  initialize: (diagramId) => {
    const dismissedInsightIds = loadDismissedInsights(diagramId);
    set({ diagramId, dismissedInsightIds, insights: [] });
  },

  setInsights: (insights) => set({ insights }),

  dismissInsight: (id) => {
    const { diagramId, dismissedInsightIds, insights } = get();
    const updatedDismissed = new Set(dismissedInsightIds);
    updatedDismissed.add(id);

    const updatedInsights = insights.map((i) =>
      i.id === id ? { ...i, dismissedAt: new Date() } : i
    );

    set({ dismissedInsightIds: updatedDismissed, insights: updatedInsights });
    if (diagramId) saveDismissedInsights(diagramId, updatedDismissed);
  },

  dismissAll: () => {
    const { diagramId, dismissedInsightIds, insights } = get();
    const now = new Date();
    const allIds = new Set([...dismissedInsightIds, ...insights.map((i) => i.id)]);
    const updatedInsights = insights.map((i) => ({ ...i, dismissedAt: now }));

    set({ dismissedInsightIds: allIds, insights: updatedInsights });
    if (diagramId) saveDismissedInsights(diagramId, allIds);
  },

  undismissInsight: (id) => {
    const { diagramId, dismissedInsightIds, insights } = get();
    const updatedDismissed = new Set(dismissedInsightIds);
    updatedDismissed.delete(id);

    const updatedInsights = insights.map((i) =>
      i.id === id ? { ...i, dismissedAt: undefined } : i
    );

    set({ dismissedInsightIds: updatedDismissed, insights: updatedInsights });
    if (diagramId) saveDismissedInsights(diagramId, updatedDismissed);
  },

  clearDismissed: () => {
    const { diagramId, insights } = get();
    const updatedInsights = insights.map((i) => ({ ...i, dismissedAt: undefined }));
    set({ dismissedInsightIds: new Set(), insights: updatedInsights });
    if (diagramId) saveDismissedInsights(diagramId, new Set());
  },

  getVisibleInsights: () => {
    const { insights, dismissedInsightIds } = get();
    return insights
      .filter((i) => !dismissedInsightIds.has(i.id))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
  },
}));
